import React from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { PawPrint, Warning, ShieldCheck, ShieldWarning } from "@phosphor-icons/react";

// Pet Toppers safety + Guaranteed-Analysis inputs panel. Shows the pet-toxicity
// scan and lets the maker enter measured Moisture / Ash for the GA + calorie
// math. Advisory only — not a veterinary or AAFCO review.
export default function PetSafetyPanel({ pet, moisturePct, setMoisturePct, ashPct, setAshPct }) {
  const flags = pet?.safety_flags || [];
  const dangers = flags.filter((f) => f.level === "danger");
  const cautions = flags.filter((f) => f.level === "caution");

  return (
    <Card className="p-5 border-orange-200 ring-1 ring-inset ring-orange-200 bg-orange-50/40" data-testid="pet-safety-panel">
      <div className="flex items-start gap-2">
        <div className="h-9 w-9 rounded-md bg-orange-100 text-orange-800 grid place-items-center shrink-0">
          <PawPrint size={18} weight="duotone" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700">Pet Toppers</div>
          <h3 className="font-semibold mt-0.5">Pet-safety scan &amp; Guaranteed Analysis inputs</h3>
          <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
            Screens for ingredients commonly toxic to dogs &amp; cats and builds the AAFCO Guaranteed
            Analysis. Calculated estimates — confirm with a lab proximate analysis and a veterinary review.
          </p>
        </div>
      </div>

      {/* Safety flags */}
      <div className="mt-4 space-y-2">
        {flags.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            <ShieldCheck size={16} weight="fill" />
            No known pet-toxic ingredients detected in this formula.
          </div>
        ) : (
          <>
            {dangers.map((f, i) => (
              <FlagRow key={`d-${i}`} flag={f} tone="danger" />
            ))}
            {cautions.map((f, i) => (
              <FlagRow key={`c-${i}`} flag={f} tone="caution" />
            ))}
          </>
        )}
      </div>

      {/* GA inputs */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Moisture (max %)
          </Label>
          <Input
            type="number" min="0" max="100" step="0.1"
            value={moisturePct}
            onChange={(e) => setMoisturePct(e.target.value)}
            placeholder="lab value (default 10)"
            className="h-8 mt-1"
            data-testid="pet-moisture-input"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Crude Ash (%)
          </Label>
          <Input
            type="number" min="0" max="100" step="0.1"
            value={ashPct}
            onChange={(e) => setAshPct(e.target.value)}
            placeholder="lab value (default 8)"
            className="h-8 mt-1"
            data-testid="pet-ash-input"
          />
        </div>
      </div>
      <p className="text-[10px] text-stone-500 mt-2 leading-relaxed">
        Moisture &amp; ash drive the Moisture guarantee and the calorie (ME) calculation. Enter
        measured lab values when available; defaults are used until then.
      </p>

      {pet?.disclaimer && (
        <div className="mt-3 text-[10px] text-stone-600 bg-white border border-orange-200 rounded-md px-3 py-2 leading-relaxed">
          {pet.disclaimer}
        </div>
      )}
    </Card>
  );
}

function FlagRow({ flag, tone }) {
  const danger = tone === "danger";
  return (
    <div className={`rounded-md px-3 py-2 border text-sm ${
      danger ? "bg-red-50 border-red-200 text-red-900" : "bg-amber-50 border-amber-200 text-amber-900"
    }`}>
      <div className="flex items-center gap-2 font-semibold">
        {danger ? <Warning size={15} weight="fill" /> : <ShieldWarning size={15} weight="fill" />}
        {flag.label}
        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/70">
          {danger ? "Avoid" : "Caution"}
        </span>
      </div>
      <div className="text-[11px] mt-1 leading-relaxed">
        <b>{flag.ingredient}</b> — {flag.reason}
        <span className="block text-stone-500 mt-0.5">Affects: {flag.species}</span>
      </div>
    </div>
  );
}
